import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { showSuccessToast } from '@/src/renderer/components/common/SuccessToast'
import { showUndoToast } from '@/src/renderer/components/common/UndoToast'
import { db } from '@/src/renderer/lib/db/indexeddb'
import { scheduleSync } from '@/src/renderer/lib/sync/sync-engine'
import { useTabActions } from '@/src/renderer/contexts/TabManagerContext'
import { AreaIcon } from '@/src/renderer/components/icons/AreaIcon'
import type { LucideIcon } from 'lucide-react'
import { buildTaskUpdate, type StagedAction } from '../../utils/buildTaskUpdate'
import { setPendingTaskJump } from '../../utils/pendingTaskJump'

interface ReactivateTarget {
  id: string
}

/**
 * Commits a single Reactivate-Dropdown action against the given task —
 * mirrors the Clarify batch-commit logic but per-action and with an
 * action-specific toast. Intentionally silent (no sound feedback) so
 * single-action flips don't compete with the toast.
 *
 * Delete is special: it grabs the full Dexie row first, soft-deletes,
 * and shows an Undo toast that restores the snapshot — same pattern as
 * `useTaskOperations.handleDeleteTask`. wakeInProject is also special:
 * its toast carries an "open project" action button so the user can
 * jump to the project tab right after waking the task.
 */
export function useReactivateAction(task: ReactivateTarget | null | undefined) {
  const { t, i18n } = useTranslation()
  const { openTab } = useTabActions()
  const locale = i18n.language === 'en' ? 'en-US' : 'de-DE'

  return useCallback(
    async (action: StagedAction) => {
      if (!task) return

      const now = new Date()

      if (action.kind === 'delete') {
        // Capture the full row so Undo can put it back exactly as it
        // was. Order matters: read BEFORE the delete write.
        const snapshot = await db.tasks.get(task.id)
        await db.deleteTask(task.id)
        scheduleSync()

        if (snapshot) {
          showUndoToast({
            message: t('dormant.menu.toastDelete'),
            undoLabel: t('common.undo'),
            onUndo: async () => {
              await db.tasks.put({ ...snapshot, deletedAt: null, synced: false })
              await db.queueSyncChange(
                snapshot.cloudId ? 'update' : 'create',
                'tasks',
                { localId: snapshot.id, cloudId: snapshot.cloudId },
              )
              scheduleSync()
            },
          })
        }
        return
      }

      const updates = buildTaskUpdate(action, now)
      await db.updateTask(task.id, updates)
      scheduleSync()

      const message = formatToast(action, t, locale)
      if (!message) return

      if (action.kind === 'wakeInProject') {
        // Snapshot at toast-creation time so the closure stays correct
        // even if the underlying task changes later.
        const taskId = task.id
        const { projectId, projectName, lifeSpace } = action
        showSuccessToast({
          message,
          action: {
            label: t('dormant.menu.toastOpenProject', { project: projectName }),
            onClick: () => {
              // Stash before openTab — ProjectPage reads the value on
              // data-ready (works whether the tab is created fresh or
              // re-activated) and the path stays free of query params
              // so useTabManager dedupes correctly.
              setPendingTaskJump(projectId, taskId)
              openTab({
                id: `${lifeSpace}-project-${projectId}-${Date.now()}`,
                path: `/home/${lifeSpace}/project/${projectId}`,
                title: projectName,
                icon: AreaIcon as unknown as LucideIcon,
                lifeSpace,
                isDirty: false,
                isPinned: false,
              })
            },
          },
        })
        return
      }

      showSuccessToast({ message })
    },
    [task, t, locale, openTab],
  )
}

function formatToast(
  action: StagedAction,
  t: ReturnType<typeof useTranslation>['t'],
  locale: string,
): string | null {
  switch (action.kind) {
    case 'wakeInProject':
      return t('dormant.menu.toastWakeInProject')
    case 'today':
      return t('dormant.menu.toastToday')
    case 'tomorrow':
      return t('dormant.menu.toastTomorrow')
    case 'later':
      return t('dormant.menu.toastLater', {
        date: action.date.toLocaleDateString(locale, {
          day: 'numeric',
          month: 'short',
        }),
      })
    case 'move':
      return t('dormant.menu.toastMove', { project: action.projectName })
    case 'delete':
      // Delete uses showUndoToast above; never reaches this branch.
      return null
    case 'pause':
      // Reactivate dropdown never offers pause; defensive no-op.
      return null
  }
}
